import { useEffect, useMemo, useRef, useState } from "react";
import {
  NodeViewContent,
  NodeViewProps,
  NodeViewWrapper,
} from "@tiptap/react";
import {
  ActionIcon,
  Group,
  SegmentedControl,
  Tabs as MantineTabs,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { IconPencil, IconPlus, IconTrash } from "@tabler/icons-react";
import classes from "./tabs.module.css";

type TabChild = { label: string; start: string | null; end: string | null };

function useTabChildren(node: NodeViewProps["node"]): TabChild[] {
  return useMemo(() => {
    const list: TabChild[] = [];
    node.forEach((child) => {
      if (child.type.name === "tab") {
        list.push({
          label: (child.attrs?.label as string) || "Tab",
          start: (child.attrs?.start as string | null) ?? null,
          end: (child.attrs?.end as string | null) ?? null,
        });
      }
    });
    return list;
  }, [node]);
}

// Period dates are entered and shown in Moscow time — the time the game
// announces events in. Moscow has had no DST since 2014, so a fixed +03:00
// round-trips exactly.
const MSK_OFFSET_MS = 3 * 60 * 60 * 1000;

function toMskInput(iso: string | null): string {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  return new Date(t + MSK_OFFSET_MS).toISOString().slice(0, 16);
}

function fromMskInput(value: string): string | null {
  return value ? `${value}:00+03:00` : null;
}

export default function TabsView(props: NodeViewProps) {
  const { node, editor, getPos } = props;
  const tabs = useTabChildren(node);
  const [active, setActive] = useState<string>("0");
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const editableRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const idx = parseInt(active, 10);
    if (!Number.isFinite(idx) || idx >= tabs.length) {
      setActive(tabs.length ? "0" : "0");
    }
  }, [tabs.length, active]);

  useEffect(() => {
    if (editingIndex === null) return;
    const el = editableRef.current;
    if (!el) return;
    // Move keyboard focus into the contentEditable span and select all
    // existing text so the user can immediately type to replace.
    el.focus();
    const range = document.createRange();
    range.selectNodeContents(el);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
  }, [editingIndex]);

  const activeIndex = Math.max(
    0,
    Math.min(parseInt(active, 10) || 0, Math.max(0, tabs.length - 1)),
  );

  // Tab visibility is now handled purely via CSS using the
  // `data-active-index` attribute on NodeViewWrapper + :nth-of-type()
  // rules in tabs.module.css. No DOM-observer loop, no feedback with PM.

  const canEdit = editor.isEditable;
  // `periods`: a recurring event's runs (one tab per month). Same node,
  // different public rendering — see apps/public/components/periods-block.tsx.
  const isPeriods = node.attrs?.variant === "periods";
  const noun = isPeriods ? "period" : "tab";

  /** Document position of the index-th `tab` child, or null. */
  const tabPos = (index: number): number | null => {
    if (typeof getPos !== "function") return null;
    const parentPos = getPos();
    if (parentPos == null) return null;
    let offset = parentPos + 1;
    let found: number | null = null;
    let i = 0;
    node.forEach((child) => {
      if (child.type.name === "tab") {
        if (i === index) found = offset;
        i++;
      }
      offset += child.nodeSize;
    });
    return found;
  };

  const setVariant = (variant: string) => {
    if (typeof getPos !== "function") return;
    const pos = getPos();
    if (pos == null) return;
    editor.view.dispatch(
      editor.state.tr.setNodeMarkup(pos, undefined, { ...node.attrs, variant }),
    );
  };

  const setPeriodDate = (
    index: number,
    key: "start" | "end",
    value: string,
  ) => {
    const pos = tabPos(index);
    if (pos == null) return;
    editor.view.dispatch(
      editor.state.tr.setNodeMarkup(pos, undefined, {
        ...node.child(index).attrs,
        [key]: fromMskInput(value),
      }),
    );
  };

  const commitRename = (index: number, value: string) => {
    if (typeof getPos !== "function") return;
    const parentPos = getPos();
    if (parentPos == null) return;
    let offset = parentPos + 1;
    let found: number | null = null;
    let i = 0;
    node.forEach((child) => {
      if (child.type.name === "tab") {
        if (i === index) found = offset;
        i++;
      }
      offset += child.nodeSize;
    });
    if (found == null) return;
    const tr = editor.state.tr.setNodeMarkup(found, undefined, {
      ...node.child(index).attrs,
      label: value || "Tab",
    });
    editor.view.dispatch(tr);
  };

  const addTab = () => {
    if (typeof getPos !== "function") return;
    const parentPos = getPos();
    if (parentPos == null) return;
    const insertAt = parentPos + node.nodeSize - 1;
    // A new period starts as a copy of the latest one: month to month only
    // the dates and some rewards change, and rebuilding a 28-row table by
    // hand every month is the chore this block exists to remove.
    const last = isPeriods ? node.lastChild : null;
    const copied = last?.type.name === "tab" ? last.toJSON().content : null;
    editor
      .chain()
      .focus()
      .insertContentAt(insertAt, {
        type: "tab",
        attrs: isPeriods
          ? { label: `Period ${tabs.length + 1}`, start: null, end: null }
          : { label: `Tab ${tabs.length + 1}` },
        content: copied?.length ? copied : [{ type: "paragraph" }],
      })
      .run();
    setActive(String(tabs.length));
  };

  const removeTab = (index: number) => {
    if (tabs.length <= 1) return;
    if (typeof getPos !== "function") return;
    const parentPos = getPos();
    if (parentPos == null) return;
    let offset = parentPos + 1;
    let i = 0;
    let target: { from: number; to: number } | null = null;
    node.forEach((child) => {
      if (child.type.name === "tab") {
        if (i === index) {
          target = { from: offset, to: offset + child.nodeSize };
        }
        i++;
      }
      offset += child.nodeSize;
    });
    if (!target) return;
    const tr = editor.state.tr.delete(target.from, target.to);
    editor.view.dispatch(tr);
    setActive(String(Math.max(0, index - 1)));
  };

  return (
    <NodeViewWrapper
      className={classes.wrapper}
      data-type="tabs"
      data-active-index={activeIndex}
    >
      <MantineTabs
        value={active}
        onChange={(v) => {
          if (!v) return;
          setActive(v);
          const idx = parseInt(v, 10);
          if (!Number.isFinite(idx)) return;
          // While renaming, do NOT move focus into the editor body —
          // that would steal focus from the contentEditable label span.
          if (editingIndex !== null) return;
          // Move cursor into the newly active tab so the slash menu
          // and typing operate inside the visible tab body.
          if (typeof getPos !== "function") return;
          const parentPos = getPos();
          if (parentPos == null) return;
          let offset = parentPos + 1;
          let i = 0;
          let insidePos: number | null = null;
          node.forEach((child) => {
            if (child.type.name === "tab") {
              if (i === idx) insidePos = offset + 1;
              i++;
            }
            offset += child.nodeSize;
          });
          if (insidePos != null) {
            queueMicrotask(() => {
              editor.chain().focus(insidePos!).run();
            });
          }
        }}
        variant="outline"
        classNames={{ list: classes.list, tab: classes.tab }}
      >
        {/* contentEditable=false keeps ProseMirror out of the tab-strip
            chrome — PM otherwise treats clicks here as selection changes
            and competes with React handlers. The actual editable tab body
            is rendered below via NodeViewContent and stays editable. */}
        <div contentEditable={false} suppressContentEditableWarning>
          <MantineTabs.List>
            {tabs.map((tab, index) => (
              <MantineTabs.Tab
                key={index}
                value={String(index)}
                onDoubleClick={() => {
                  if (!canEdit) return;
                  setEditingIndex(index);
                }}
              >
                {/* Always render a span; when this tab is being renamed,
                    flip it to contentEditable so the user types directly
                    on the label. A contentEditable span inside a <button>
                    is well-supported (unlike a nested <input>, which most
                    browsers refuse to focus). */}
                <span
                  ref={editingIndex === index ? editableRef : undefined}
                  contentEditable={editingIndex === index}
                  suppressContentEditableWarning
                  spellCheck={false}
                  className={
                    editingIndex === index ? classes.labelEditing : undefined
                  }
                  onMouseDown={(e) => {
                    if (editingIndex === index) {
                      // Prevent the browser default of transferring focus
                      // to the parent <button> on mousedown — that's what
                      // was making focus "jump out" of the label span.
                      e.preventDefault();
                      e.stopPropagation();
                      // Re-focus the span ourselves on the next tick
                      // (preventDefault on mousedown also blocks our own
                      // intended focus, so we restore it explicitly).
                      const target = e.currentTarget;
                      queueMicrotask(() => target?.focus());
                    }
                  }}
                  onPointerDown={(e) => {
                    if (editingIndex === index) e.stopPropagation();
                  }}
                  onClick={(e) => {
                    if (editingIndex === index) e.stopPropagation();
                  }}
                  onBlur={(e) => {
                    if (editingIndex !== index) return;
                    const value = (e.currentTarget.textContent ?? "").trim();
                    commitRename(index, value);
                    setEditingIndex(null);
                  }}
                  onKeyDown={(e) => {
                    if (editingIndex !== index) return;
                    if (e.key === "Enter") {
                      e.preventDefault();
                      (e.currentTarget as HTMLSpanElement).blur();
                    } else if (e.key === "Escape") {
                      e.preventDefault();
                      // Restore original label visually before bailing out.
                      e.currentTarget.textContent = tab.label || `Tab ${index + 1}`;
                      setEditingIndex(null);
                    }
                  }}
                >
                  {tab.label || `Tab ${index + 1}`}
                </span>
                {canEdit && editingIndex !== index && (
                  <Tooltip label={`Rename ${noun}`} withArrow>
                    <ActionIcon
                      component="span"
                      variant="subtle"
                      color="gray"
                      size="xs"
                      ml={4}
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingIndex(index);
                      }}
                    >
                      <IconPencil size={12} />
                    </ActionIcon>
                  </Tooltip>
                )}
                {canEdit && tabs.length > 1 && editingIndex !== index && (
                  <Tooltip label={`Remove ${noun}`} withArrow>
                    <ActionIcon
                      component="span"
                      variant="subtle"
                      color="gray"
                      size="xs"
                      ml={4}
                      onClick={(e) => {
                        e.stopPropagation();
                        removeTab(index);
                      }}
                    >
                      <IconTrash size={12} />
                    </ActionIcon>
                  </Tooltip>
                )}
              </MantineTabs.Tab>
            ))}
            {canEdit && (
              <Tooltip
                label={isPeriods ? "Add period (copies the last one)" : "Add tab"}
                withArrow
              >
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="sm"
                  onClick={addTab}
                  ml={4}
                  mt={4}
                >
                  <IconPlus size={14} />
                </ActionIcon>
              </Tooltip>
            )}
          </MantineTabs.List>
          {canEdit && (
            <Group gap="xs" mt={6} wrap="wrap">
              <SegmentedControl
                size="xs"
                value={isPeriods ? "periods" : "tabs"}
                onChange={setVariant}
                data={[
                  { label: "Tabs", value: "tabs" },
                  { label: "Event periods", value: "periods" },
                ]}
              />
              {isPeriods && tabs[activeIndex] && (
                <>
                  <TextInput
                    size="xs"
                    type="datetime-local"
                    label="Start (MSK)"
                    value={toMskInput(tabs[activeIndex].start)}
                    onChange={(e) =>
                      setPeriodDate(activeIndex, "start", e.currentTarget.value)
                    }
                  />
                  <TextInput
                    size="xs"
                    type="datetime-local"
                    label="End (MSK)"
                    value={toMskInput(tabs[activeIndex].end)}
                    onChange={(e) =>
                      setPeriodDate(activeIndex, "end", e.currentTarget.value)
                    }
                  />
                </>
              )}
            </Group>
          )}
        </div>
      </MantineTabs>

      <NodeViewContent
        className={classes.content}
        as="div"
        data-active-index={activeIndex}
      />
    </NodeViewWrapper>
  );
}
